import React, { useEffect, useState } from "react";
import {
  fetchFleetDataByUserIds,
  fetchMasterAccessByCode,
  fetchPhasesByUserIds,
  fetchControlsByUserIds,
  fetchAdvancedControlsByUserIds,
  fetchDefaultDataByUserIds,
  fetchCityInfoByUserIds,
  fetchChargerCostsByUserIds,
} from "api/masteraccessapi";
import { checkUsersExist } from "api/adminapi";
import { useNavigate, useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import OverviewCard from "./OverviewCard";
import useProForma from "store/useProForma";
import useAuthStore from "store/useAuthStore";
import { calcYears } from "utils/proFormaCalcs";
import { getCumulativeCostBenefitAtEvalForMaxVehiclesScenario } from "utils/cumulativeCostBenefitMaxVehiclesScenario";
import ToggleNav from "components/ToggleNav";

const MasterDashboard = () => {
  const navigate = useNavigate();
  const [fleetSummary, setFleetSummary] = useState({
    totalEVsVehicles: 0,
    totalPorts: 0,
    totalMarginalCosts: 0,
    totalGHGEmissionsSaved: 0,
  });
  const [isLoadingFleet, setIsLoadingFleet] = useState(true);
  const [fleetError, setFleetError] = useState(null);
  const [userIds, setUserIds] = useState([]);
  const [usernames, setUsernames] = useState([]);
  const [userIdToUsername, setUserIdToUsername] = useState({});
  const [hasResolvedUserIds, setHasResolvedUserIds] = useState(false);
  const [lastAssessmentCreatedAtByUsername, setLastAssessmentCreatedAtByUsername] = useState({});
  const [evCountByUsernameDomicile, setEvCountByUsernameDomicile] = useState({});
  const cityNameByUsername = useProForma((s) => s.cityNameByUsername);
  const setCityNameByUsername = useProForma((s) => s.setCityNameByUsername);
  const [userIdsError, setUserIdsError] = useState(null);
  const [programName, setProgramName] = useState(null);
  const { cityAccess, parentData } = useProForma();

  const location = useLocation();
  const { code } = location.state || {};

  useEffect(() => {
    if (!code) {
      navigate("/login"); // Redirect to /login if code is missing
      return;
    }
  }, [code, navigate]);

  useEffect(() => {
    if (!code) {
      return;
    }

    let isMounted = true;
    const fetchUserIds = async () => {
      setUserIdsError(null);
      setHasResolvedUserIds(false);
      const data = await fetchMasterAccessByCode(code);

      if (!isMounted) return;
      if (!data?.error && data?.cityAccess) {
        setProgramName(data.cityAccess.program_name ?? null);
      } else {
        setProgramName(null);
      }
      const listData = data?.error ? null : data?.cityAccessList;
      const usernames = Array.isArray(listData)
        ? Array.from(new Set(listData.map((item) => item?.username).filter(Boolean)))
        : [];
      if (usernames.length === 0) {
        setUserIds([]);
        setUsernames([]);
        setUserIdToUsername({});
        setHasResolvedUserIds(true);
        return;
      }

      setUsernames(usernames);

      const response = await checkUsersExist(usernames);

      if (!isMounted) return;
      if (response.error || !response.results) {
        setUserIds([]);
        setUserIdToUsername({});
        setHasResolvedUserIds(true);
        return;
      }
      const idList = [];
      const uidToUser = {};
      for (const r of response.results) {
        if (r.exists && r.userId) {
          idList.push(r.userId);
          uidToUser[r.userId] = String(r.username || "").trim();
        }
      }
      setUserIds(idList);
      setUserIdToUsername(uidToUser);
      setHasResolvedUserIds(true);
    };
    fetchUserIds();
    return () => {
      isMounted = false;
    };
  }, [code]);

  useEffect(() => {
    let isMounted = true;
    const fetchFleetData = async () => {
      setIsLoadingFleet(true);
      setFleetError(userIdsError);
      if (userIdsError) {
        setLastAssessmentCreatedAtByUsername({});
        setEvCountByUsernameDomicile({});
        setCityNameByUsername({});
        setIsLoadingFleet(false);
        return;
      }
      const [
        { data: fleetData, error: fleetErrorResponse },
        { data: phasesData, error: phasesErrorResponse },
      ] = await Promise.all([fetchFleetDataByUserIds(userIds), fetchPhasesByUserIds(userIds)]);

      if (!isMounted) return;
      if (fleetErrorResponse || phasesErrorResponse) {
        setFleetError(fleetErrorResponse || phasesErrorResponse);
        setFleetSummary({
          totalEVsVehicles: 0,
          totalPorts: 0,
          totalMarginalCosts: 0,
          totalGHGEmissionsSaved: 0,
        });
        setLastAssessmentCreatedAtByUsername({});
        setEvCountByUsernameDomicile({});
        setCityNameByUsername({});
        setIsLoadingFleet(false);
        return;
      }

      const totalGHGEmissionsSaved = (fleetData ?? []).reduce(
        (acc, item) => acc + (Number(item?.ghg) || 0),
        0,
      );

      // Count fleet rows per account + Simplified Domicile (same string as marker site / row.location)
      const nextEvCountByUsernameDomicile = {};

      for (const item of fleetData ?? []) {
        const uid = item.user_id;
        const uname = userIdToUsername[uid];

        const domicile = String(item["Simplified Domicile"] ?? "").trim() || "Unknown";

        if (!uname) continue;
        const key = `${String(uname).toLowerCase()}|||${domicile}`;
        nextEvCountByUsernameDomicile[key] = (nextEvCountByUsernameDomicile[key] ?? 0) + 1;
      }

      const totalEVsVehicles = fleetData?.length ?? 0;
      const totalPorts =
        phasesData?.reduce((sum, phase) => {
          const count =
            Number(phase?.port_10_20_kw) +
            Number(phase?.port_25_kw) +
            Number(phase?.port_180_200_kw) +
            Number(phase?.port_less_than_10_kw);
          return sum + (Number.isNaN(count) ? 0 : count);
        }, 0) ?? 0;

      // Sum cumulative cost-benefit (at eval year) from already-fetched fleet + phases + batch-fetched controls/advanced/default data (no getFleetData / accessToken)
      let totalMarginalCosts = 0;
      const nextLastAssessmentByUsername = {};
      let nextCityNameByUsername = {};
      if (userIds.length > 0 && (fleetData?.length > 0 || phasesData?.length > 0)) {
        const [
          { data: controlsRows },
          { data: advancedRows },
          { data: defaultDataRows },
          { data: cityInfoRows },
          { data: chargerCostsRows },
        ] = await Promise.all([
          fetchControlsByUserIds(userIds),
          fetchAdvancedControlsByUserIds(userIds),
          fetchDefaultDataByUserIds(userIds),
          fetchCityInfoByUserIds(userIds),
          fetchChargerCostsByUserIds(userIds),
        ]);
        const cityNameByUserID = (cityInfoRows ?? []).reduce((acc, row) => {
          if (row?.id != null) acc[row.id] = row.city_name;
          return acc;
        }, {});

        for (const uid of userIds) {
          const uname = userIdToUsername[uid];

          if (!uname) continue;
          const cn = cityNameByUserID[uid];
          if (cn != null && String(cn).trim() !== "") {
            nextCityNameByUsername[String(uname).toLowerCase()] = String(cn).trim();
          }
        }

        const controlsByUserId = (controlsRows ?? []).reduce((acc, row) => {
          if (row?.id != null) acc[row.id] = row;
          return acc;
        }, {});
        const advancedByUserId = (advancedRows ?? []).reduce((acc, row) => {
          if (row?.id != null) acc[row.id] = row;
          return acc;
        }, {});
        const cityInfoByUserId = (cityInfoRows ?? []).reduce((acc, row) => {
          if (row?.id != null) acc[row.id] = row;
          return acc;
        }, {});
        for (const uid of userIds) {
          const uname = userIdToUsername[uid];
          const row = cityInfoByUserId[uid];
          if (!uname || !row?.created_at) continue;
          nextLastAssessmentByUsername[String(uname).toLowerCase()] = row.created_at;
        }
        const chargerCostFromChargerTable = (chargerCostsRows ?? []).reduce((acc, row) => {
          if (row?.id != null) acc[row.id] = row;
          return acc;
        }, {});
        const chargerCostFromDefault = (defaultDataRows ?? []).reduce((acc, row) => {
          if (row?.id == null) return acc;
          const raw = row.charger_costs;
          acc[row.id] = Array.isArray(raw) ? raw[0] : raw;
          return acc;
        }, {});
        const chargerCostByUserId = Object.fromEntries(
          userIds.map((uid) => [
            uid,
            chargerCostFromChargerTable[uid] ?? chargerCostFromDefault[uid],
          ]),
        );

        const fleetByUserId = (fleetData ?? []).reduce((acc, v) => {
          const uid = v.user_id;
          if (!acc[uid]) acc[uid] = [];
          acc[uid].push(v);
          return acc;
        }, {});
        const phasesByUserId = (phasesData ?? []).reduce((acc, p) => {
          const uid = p.user_id;
          if (!acc[uid]) acc[uid] = [];
          acc[uid].push(p);
          return acc;
        }, {});

        const defaultCityInfo = {
          year_max: 2050,
          port_count_1: 1,
          port_count_2: 1,
          port_count_3: 1,
          port_count_4: 1,
        };

        const includeMasterAccessIncentives =
          !!useAuthStore.getState().masterAccessIncentiveColumnTitle;

        for (const userId of userIds) {
          try {
            let userFleet = fleetByUserId[userId] ?? [];
            const userPhasesRaw = phasesByUserId[userId] ?? [];
            const controls = controlsByUserId[userId];
            const advancedControls = advancedByUserId[userId];
            const chargerCost = chargerCostByUserId[userId];
            const cityInfo = {
              ...defaultCityInfo,
              ...(cityInfoByUserId[userId] || {}),
            };

            if (!controls || !advancedControls || !chargerCost) continue;
            if (userFleet.length === 0 || userPhasesRaw.length === 0) continue;

            userFleet = userFleet
              .map((v) => ({
                ...v,
                id: String(v.equipment_id),
              }))
              .sort((a, b) => (a.equipment_id ?? 0) - (b.equipment_id ?? 0));

            let ctrl = { ...controls };
            const uniqueDomiciles = [
              ...new Set(userFleet.map((item) => item["Simplified Domicile"])),
            ];
            ctrl.domiciles = uniqueDomiciles;
            if (ctrl.domiciles.length === 1) {
              ctrl.site = ctrl.domiciles[0];
            }
            if (ctrl.site === "") ctrl.site = "All Sites";
            if (
              ctrl.electrification_scenario === "" &&
              userFleet.length > 0 &&
              userFleet[0].electrification_scenario
            ) {
              ctrl.electrification_scenario =
                Object.keys(userFleet[0].electrification_scenario)[0] ?? "";
            }

            const years = calcYears(
              userFleet,
              userPhasesRaw,
              cityInfo.year_max ?? defaultCityInfo.year_max,
            );

            totalMarginalCosts += getCumulativeCostBenefitAtEvalForMaxVehiclesScenario(
              userFleet,
              userPhasesRaw,
              advancedControls,
              ctrl,
              cityInfo,
              chargerCost,
              years,
              includeMasterAccessIncentives,
            );
          } catch {
            // skip this user
          }
        }
      }
      setFleetSummary({
        totalEVsVehicles,
        totalPorts,
        totalMarginalCosts,
        totalGHGEmissionsSaved,
      });
      setLastAssessmentCreatedAtByUsername(nextLastAssessmentByUsername);
      setEvCountByUsernameDomicile(nextEvCountByUsernameDomicile);
      setCityNameByUsername(nextCityNameByUsername);
      setIsLoadingFleet(false);
    };
    fetchFleetData();
    return () => {
      isMounted = false;
    };
  }, [userIds, userIdsError, userIdToUsername]);

  return (
    <div className="w-full h-screen p-4">
      {/* <HeaderBar /> */}
      <div className="flex justify-between gap-2">
        <ToggleNav />
      </div>
      <div className="w-full bg-gray-50 shadow-md rounded-2xl border p-6 mt-4">
        <h2 className="text-2xl font-semibold text-gray-800">
          {(cityAccess?.organization_name || parentData?.organization_name) +
            "  " +
            "Performance Overview"}
        </h2>
        <p className="text-sm text-gray-500">
          Use this tool to inform ongoing fleet electrification decisions, including funding,
          vehicle replacement, and EV charging strategies. All values are estimated and subject to
          change.
        </p>
      </div>
      <Tabs
        defaultValue="overview"
        className="w-full mt-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {/* <TabsTrigger value="dashboard">Environmental Metrics</TabsTrigger> */}
        </TabsList>
        <TabsContent
          value="overview"
          className="mt-4">
          <OverviewCard
            fleetSummary={fleetSummary}
            isLoadingFleet={isLoadingFleet}
            fleetError={fleetError}
            usernames={usernames}
            userIds={userIds}
            userIdToUsername={userIdToUsername}
            hasResolvedUserIds={hasResolvedUserIds}
            programName={programName}
            lastAssessmentCreatedAtByUsername={lastAssessmentCreatedAtByUsername}
            evCountByUsernameDomicile={evCountByUsernameDomicile}
            cityNameByUsername={cityNameByUsername}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MasterDashboard;
